import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Mail, TrendingUp, Reply, AlertTriangle, MousePointerClick, Loader2, CalendarIcon, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getSmartleadStats, getSmartleadCampaignDetail } from "@/lib/smartlead.functions";

export const Route = createFileRoute("/dashboard/admin/comms/smartlead")({
  head: () => ({ meta: [{ title: "Smartlead Analytics · Admin" }, { name: "robots", content: "noindex" }] }),
  component: SmartleadPage,
});

type Preset = "today" | "7d" | "30d" | "custom";

function toISO(d: Date) {
  return d.toISOString().slice(0, 10);
}

function presetRange(p: Exclude<Preset, "custom">): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  if (p === "today") {
    // same day
  } else if (p === "7d") {
    start.setDate(end.getDate() - 6);
  } else {
    start.setDate(end.getDate() - 29);
  }
  return { startDate: toISO(start), endDate: toISO(end) };
}

type StatAccent = "blue" | "green" | "violet" | "red" | "cyan" | "amber" | "pink" | "slate";

const ACCENTS: Record<StatAccent, { bar: string; bg: string; text: string; iconBg: string }> = {
  blue:   { bar: "bg-blue-500",    bg: "bg-blue-50/60",    text: "text-blue-700",    iconBg: "bg-blue-100 text-blue-600" },
  green:  { bar: "bg-emerald-500", bg: "bg-emerald-50/60", text: "text-emerald-700", iconBg: "bg-emerald-100 text-emerald-600" },
  violet: { bar: "bg-violet-500",  bg: "bg-violet-50/60",  text: "text-violet-700",  iconBg: "bg-violet-100 text-violet-600" },
  red:    { bar: "bg-red-500",     bg: "bg-red-50/60",     text: "text-red-700",     iconBg: "bg-red-100 text-red-600" },
  cyan:   { bar: "bg-cyan-500",    bg: "bg-cyan-50/60",    text: "text-cyan-700",    iconBg: "bg-cyan-100 text-cyan-600" },
  amber:  { bar: "bg-amber-500",   bg: "bg-amber-50/60",   text: "text-amber-700",   iconBg: "bg-amber-100 text-amber-600" },
  pink:   { bar: "bg-pink-500",    bg: "bg-pink-50/60",    text: "text-pink-700",    iconBg: "bg-pink-100 text-pink-600" },
  slate:  { bar: "bg-slate-500",   bg: "bg-slate-50/60",   text: "text-slate-700",   iconBg: "bg-slate-100 text-slate-600" },
};

function Stat({ icon: Icon, label, value, suffix, accent = "slate" }: { icon: any; label: string; value: string | number; suffix?: string; accent?: StatAccent }) {
  const a = ACCENTS[accent];
  return (
    <div className={cn("relative overflow-hidden rounded-md border border-border p-4", a.bg)}>
      <span className={cn("absolute left-0 top-0 bottom-0 w-1", a.bar)} />
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </div>
        <span className={cn("flex items-center justify-center h-6 w-6 rounded", a.iconBg)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className={cn("mt-1.5 text-[22px] font-bold tabular-nums leading-none", a.text)}>
        {value}
        {suffix ? <span className="text-[12px] font-medium opacity-70 ml-1">{suffix}</span> : null}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = (status || "").toUpperCase();
  const tone =
    s === "ACTIVE" || s === "RUNNING"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : s === "PAUSED" || s === "STOPPED"
        ? "bg-amber-100 text-amber-700 border-amber-200"
        : s === "COMPLETED"
          ? "bg-blue-100 text-blue-700 border-blue-200"
          : s === "DRAFTED" || s === "DRAFT"
            ? "bg-slate-100 text-slate-600 border-slate-200"
            : "bg-muted text-muted-foreground border-border";
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide border", tone)}>
      {s || "—"}
    </span>
  );
}

function SmartleadPage() {
  const fetchStats = useServerFn(getSmartleadStats);
  const [preset, setPreset] = useState<Preset>("30d");
  const [customStart, setCustomStart] = useState<Date | undefined>();
  const [customEnd, setCustomEnd] = useState<Date | undefined>();

  const range = useMemo(() => {
    if (preset === "custom" && customStart && customEnd) {
      return { startDate: toISO(customStart), endDate: toISO(customEnd) };
    }
    if (preset === "custom") return presetRange("30d");
    return presetRange(preset);
  }, [preset, customStart, customEnd]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["smartlead-stats", range.startDate, range.endDate],
    queryFn: () => fetchStats({ data: range }),
    staleTime: 60_000,
  });

  const presetBtn = (p: Preset, label: string) => (
    <button
      key={p}
      onClick={() => setPreset(p)}
      className={cn(
        "text-[12.5px] px-3 py-1.5 rounded-md border transition-colors",
        preset === p ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40"
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-[18px] font-semibold tracking-tight">Smartlead Analytics</h2>
          </div>
          <p className="text-[13px] text-muted-foreground mt-1">
            {range.startDate} → {range.endDate}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {presetBtn("today", "Today")}
          {presetBtn("7d", "Last 7 days")}
          {presetBtn("30d", "Last 30 days")}
          {presetBtn("custom", "Custom")}
          {preset === "custom" && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-[12.5px] gap-2">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {customStart ? format(customStart, "MMM d, yyyy") : "Start"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customStart} onSelect={setCustomStart} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-[12.5px] gap-2">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {customEnd ? format(customEnd, "MMM d, yyyy") : "End"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </>
          )}
          <button
            onClick={() => refetch()}
            className="text-[12.5px] rounded-md border border-border bg-card px-3 py-1.5 hover:border-primary/40"
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Smartlead stats…
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-[13px] text-destructive">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {data && "error" in data && data.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-[13px] text-destructive">
          {data.error}
        </div>
      )}

      {data && "totals" in data && data.totals && data.campaigns && data.daily && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Mail} label="Emails sent" value={data.totals.sent.toLocaleString()} accent="blue" />
            <Stat icon={TrendingUp} label="Open rate" value={data.totals.openRate} suffix="%" accent="green" />
            <Stat icon={Reply} label="Reply rate" value={data.totals.replyRate} suffix="%" accent="violet" />
            <Stat icon={AlertTriangle} label="Bounce rate" value={data.totals.bounceRate} suffix="%" accent="red" />
            <Stat icon={TrendingUp} label="Unique opens" value={data.totals.uniqueOpens.toLocaleString()} accent="cyan" />
            <Stat icon={MousePointerClick} label="Clicks" value={data.totals.clicks.toLocaleString()} accent="amber" />
            <Stat icon={Reply} label="Replies" value={data.totals.replies.toLocaleString()} accent="pink" />
            <Stat icon={AlertTriangle} label="Bounces" value={data.totals.bounces.toLocaleString()} accent="red" />
          </div>

          <div className="rounded-md border border-border bg-card p-4">
            <h3 className="text-[14px] font-semibold mb-3">Daily activity</h3>
            <div className="w-full h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.daily} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="sent" stroke="#3b82f6" strokeWidth={2} dot={false} name="Sent" />
                  <Line type="monotone" dataKey="uniqueOpens" stroke="#10b981" strokeWidth={2} dot={false} name="Opens" />
                  <Line type="monotone" dataKey="replies" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Replies" />
                  <Line type="monotone" dataKey="bounces" stroke="#ef4444" strokeWidth={2} dot={false} name="Bounces" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {data.daily.length === 0 && (
              <p className="text-center text-[12.5px] text-muted-foreground mt-3">No activity in this range.</p>
            )}
          </div>

          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-[14px] font-semibold">Campaigns ({data.campaigns.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="w-8 px-2 py-2"></th>
                    <th className="text-left px-4 py-2 font-medium">Campaign</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">Sent</th>
                    <th className="text-right px-4 py-2 font-medium">Opens</th>
                    <th className="text-right px-4 py-2 font-medium">Replies</th>
                    <th className="text-right px-4 py-2 font-medium">Bounces</th>
                    <th className="text-right px-4 py-2 font-medium">Open %</th>
                    <th className="text-right px-4 py-2 font-medium">Reply %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map((c) => (
                    <CampaignRow key={c.id} c={c} range={range} />
                  ))}
                  {data.campaigns.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">
                        No campaigns found in Smartlead.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CampaignRow({
  c,
  range,
}: {
  c: { id: number; name: string; status: string; sent: number; uniqueOpens: number; replies: number; bounces: number; openRate: number; replyRate: number };
  range: { startDate: string; endDate: string };
}) {
  const [open, setOpen] = useState(false);
  const fetchDetail = useServerFn(getSmartleadCampaignDetail);
  const { data, isLoading, error } = useQuery({
    queryKey: ["smartlead-campaign", c.id, range.startDate, range.endDate],
    queryFn: () => fetchDetail({ data: { campaignId: c.id, startDate: range.startDate, endDate: range.endDate } }),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <>
      <tr className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="px-2 py-2 text-muted-foreground">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-4 py-2 font-medium">{c.name}</td>
        <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
        <td className="px-4 py-2 text-right">{c.sent.toLocaleString()}</td>
        <td className="px-4 py-2 text-right">{c.uniqueOpens.toLocaleString()}</td>
        <td className="px-4 py-2 text-right">{c.replies.toLocaleString()}</td>
        <td className="px-4 py-2 text-right">{c.bounces.toLocaleString()}</td>
        <td className="px-4 py-2 text-right">{c.openRate}%</td>
        <td className="px-4 py-2 text-right">{c.replyRate}%</td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={9} className="px-4 py-4">
            {isLoading && (
              <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading campaign detail…
              </div>
            )}
            {error && <div className="text-[12.5px] text-destructive">Failed: {(error as Error).message}</div>}
            {data && "error" in data && data.error && (
              <div className="text-[12.5px] text-destructive">{data.error}</div>
            )}
            {data && "summary" in data && data.summary && (
              <div className="space-y-4">
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 lg:grid-cols-6">
                  <MiniStat label="Total leads" value={data.summary.totalLeads.toLocaleString()} />
                  <MiniStat label="Sent" value={data.summary.sent.toLocaleString()} />
                  <MiniStat label="Unique sent" value={data.summary.uniqueSent.toLocaleString()} />
                  <MiniStat label="Opens" value={data.summary.uniqueOpens.toLocaleString()} />
                  <MiniStat label="Clicks" value={data.summary.clicks.toLocaleString()} />
                  <MiniStat label="Replies" value={data.summary.replies.toLocaleString()} />
                  <MiniStat label="Bounces" value={data.summary.bounces.toLocaleString()} />
                  <MiniStat label="Open rate" value={`${data.summary.openRate}%`} />
                  <MiniStat label="Click rate" value={`${data.summary.clickRate}%`} />
                  <MiniStat label="Reply rate" value={`${data.summary.replyRate}%`} />
                  <MiniStat label="Bounce rate" value={`${data.summary.bounceRate}%`} />
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <h4 className="text-[12.5px] font-semibold mb-2">Daily activity</h4>
                  <div className="w-full h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.daily} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="sent" stroke="#3b82f6" strokeWidth={2} dot={false} name="Sent" />
                        <Line type="monotone" dataKey="uniqueOpens" stroke="#10b981" strokeWidth={2} dot={false} name="Opens" />
                        <Line type="monotone" dataKey="replies" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Replies" />
                        <Line type="monotone" dataKey="bounces" stroke="#ef4444" strokeWidth={2} dot={false} name="Bounces" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {data.daily.length === 0 && (
                    <p className="text-center text-[12px] text-muted-foreground mt-2">No activity in this range.</p>
                  )}
                </div>
                {data.sequences && data.sequences.length > 0 && (
                  <div className="rounded-md border border-border bg-card p-3">
                    <h4 className="text-[12.5px] font-semibold mb-2">Sequence ({data.sequences.length} steps)</h4>
                    <ol className="space-y-1.5">
                      {data.sequences.map((s: { step: number; subject: string; delayDays: number }) => (
                        <li key={s.step} className="text-[12px] flex gap-3">
                          <span className="text-muted-foreground w-12">Step {s.step}</span>
                          <span className="flex-1">{s.subject || <em className="text-muted-foreground">no subject</em>}</span>
                          <span className="text-muted-foreground">+{s.delayDays}d</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[10.5px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-[14px] font-semibold mt-0.5">{value}</div>
    </div>
  );
}
